import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"

export type GenerationStatus = "running" | "completed" | "failed"

export interface RunState {
  status: GenerationStatus
  title: string
  /** Wall-clock start (epoch ms) of this run, so the elapsed timer reflects the
   *  real duration across case/tab switches and page reloads — not a fresh 0
   *  on every remount. */
  startedAt: number
  /** Per-case progress — switching away/back AND refreshing keeps the steps,
   *  because the whole store is persisted to localStorage. */
  completedStages: string[]
  currentStage: string | null
  currentSubstage: string | null
  /** The accumulated live "thinking" transcript for this case. Persisted with
   *  the run so leaving a case and returning shows the reasoning intact (like
   *  ChatGPT/Claude) instead of restarting from the first activity. */
  thoughts: string[]
  /** Epoch ms of the last observed REAL activity for this run (a checkpoint
   *  advance, streamed message, or an active CopilotKit stream). If this goes
   *  stale while status is "running", the run has likely silently disconnected
   *  or died — the UI surfaces a "stalled" affordance instead of a perpetual
   *  spinner. Reset to a fresh grace window when the case is (re)opened. */
  heartbeatAt: number
}

/** Max transcript lines kept per case. Generous — the full ordered history is
 *  retained for correct dedup/resume; the UI itself only renders the last few. */
const MAX_THOUGHTS = 60

interface GenerationState {
  /** Per-case run state, keyed by thread id. Persisted to localStorage so it
   *  survives the CopilotKit provider re-key on case switch, the reconnection
   *  gap, AND a full page reload — without this, progress falls back to the
   *  first stage ("Reading the case documents") on every remount. */
  runs: Record<string, RunState>
  /** Begin (or restart) tracking a run — resets its progress. */
  begin: (id: string, title: string) => void
  setStatus: (id: string, status: GenerationStatus, title: string) => void
  setProgress: (
    id: string,
    p: { completedStages?: string[]; currentStage?: string | null; currentSubstage?: string | null },
  ) => void
  /** Append a live "thinking" line for a case (deduped, rolling-window). */
  appendThought: (id: string, thought: string) => void
  /** Mark observed activity for a case (refreshes the stall heartbeat). */
  heartbeat: (id: string, ts: number) => void
  /** Drop a case's run entirely (e.g. when the case is deleted) so the global
   *  "a draft is generating" indicator clears. */
  remove: (id: string) => void
}

export const useGenerationStore = create<GenerationState>()(
  persist(
    (set) => ({
      runs: {},
      begin: (id, title) =>
        set((s) => ({
          runs: {
            ...s.runs,
            [id]: {
              status: "running",
              title,
              startedAt: Date.now(),
              completedStages: [],
              currentStage: null,
              currentSubstage: null,
              thoughts: [],
              heartbeatAt: Date.now(),
            },
          },
        })),
      setStatus: (id, status, title) =>
        set((s) => {
          const prev = s.runs[id] ?? {
            status,
            title,
            startedAt: Date.now(),
            completedStages: [],
            currentStage: null,
            currentSubstage: null,
            thoughts: [],
            heartbeatAt: Date.now(),
          }
          return { runs: { ...s.runs, [id]: { ...prev, status, title } } }
        }),
      appendThought: (id, thought) =>
        set((s) => {
          const prev = s.runs[id]
          if (!prev) return s
          // Dedup against the whole window so reconnects/remounts that re-walk a
          // stage's activities don't duplicate lines.
          if ((prev.thoughts ?? []).includes(thought)) return s
          const thoughts = [...(prev.thoughts ?? []), thought].slice(-MAX_THOUGHTS)
          return { runs: { ...s.runs, [id]: { ...prev, thoughts } } }
        }),
      setProgress: (id, p) =>
        set((s) => {
          const prev = s.runs[id]
          if (!prev) return s
          // Monotonic union — a reconnect that re-sends an empty completed list
          // must never shrink the progress we've already shown.
          const merged = Array.from(
            new Set([...prev.completedStages, ...(p.completedStages ?? [])]),
          )
          // A real progress change (a new completed stage / stage transition) is
          // observed activity → refresh the stall heartbeat.
          const changed =
            merged.length !== prev.completedStages.length ||
            (p.currentStage != null && p.currentStage !== prev.currentStage) ||
            (p.currentSubstage != null && p.currentSubstage !== prev.currentSubstage)
          return {
            runs: {
              ...s.runs,
              [id]: {
                ...prev,
                completedStages: merged,
                currentStage: p.currentStage ?? prev.currentStage,
                currentSubstage: p.currentSubstage ?? prev.currentSubstage,
                heartbeatAt: changed ? Date.now() : prev.heartbeatAt,
              },
            },
          }
        }),
      heartbeat: (id, ts) =>
        set((s) => {
          const prev = s.runs[id]
          if (!prev || prev.heartbeatAt === ts) return s
          return { runs: { ...s.runs, [id]: { ...prev, heartbeatAt: ts } } }
        }),
      remove: (id) =>
        set((s) => {
          if (!s.runs[id]) return s
          const next = { ...s.runs }
          delete next[id]
          return { runs: next }
        }),
    }),
    {
      name: "oio-generation",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ runs: s.runs }),
    },
  ),
)

// ── Cross-tab sync ─────────────────────────────────────────────────────────
// zustand persist only reads localStorage on init. When the draft runs in one
// tab, its progress is written to localStorage continuously — so other tabs of
// the same account follow along live by re-hydrating on the `storage` event
// (which fires in OTHER tabs when this key changes). Without this, a second tab
// stays frozen at the first stage even though the run is well underway.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "oio-generation" && e.newValue) {
      void useGenerationStore.persist.rehydrate()
    }
  })
}

/** Run state for a specific case (undefined if none tracked). */
export function runFor(runs: Record<string, RunState>, id: string | null | undefined) {
  return id ? runs[id] : undefined
}
